import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import TagPetCover from '../../common/TagPetCover';
import { BILLING_CYCLE_TYPE, POWERUP_TYPE } from '../../../constants';
import { currencyFormat, getAgeInYears } from '../../../util';
import { useProp103Features, useTranslations } from '../../../hooks';
import expandIcon from '../../../assets/yourInfo/plus.svg';
import collapseIcon from '../../../assets/yourInfo/substract.svg';
import './PaymentBreakdown.css';

const BreakdownItem = ({
  bold = false,
  label,
  noCapitalize = false,
  toBottom = false,
  value,
}) => (
  <li
    className="Payment-breakdown-list-item"
    data-bold={bold}
    data-noCapitalize={noCapitalize}
    data-toBottom={toBottom}
  >
    <span>{label}</span>

    <span>{value}</span>
  </li>
);

const PetBreakdown = ({ isMonthlyPlan = false, quoteRate }) => {
  const { t } = useTranslation('enrollment');
  const [isExpanded, setIsExpanded] = useState(true);
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const isProp103 = useProp103Features();

  const {
    data,
    paymentMethod,
    petTags,
    quoteSelectedValues,
    rateSummary,
  } = store;

  const quote = quoteSelectedValues
    .find((pet) => pet.quoteId === quoteRate.petQuoteId);

  const petTag =
    petTags.find((item) => item.petQuoteId === quote.quoteId) || {};

  const { paymentMethodType } = paymentMethod;

  const handleExpanded = useCallback(() => {
    setIsExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const modifiersMapped = useMemo(() => {
    const modifiers = quoteRate.InsuranceModifiersEB || [];

    const powerUpsFilter = modifiers
      .filter((modifier) => modifier.InsuranceModifierTypeEBId === POWERUP_TYPE
        && modifier.IsVisible)
      .map((powerUp) => {
        const coverageOptions = powerUp.BundleInsuranceModifiersEB
          ?.filter((modifier) => modifier.IsVisible && modifier.IsActive);

        const label = String(powerUp.TitleText).toLowerCase();

        const powerupBaseResult = {
          id: powerUp.Id,
          label: isProp103
            ? t('paymentBreakdown.modifierAddOn', { modifier: label })
            : label,
          price: powerUp.FeeAmount,
          selected: powerUp.IsSelected,
        };

        if (coverageOptions?.length > 1) {
          const selectedBundle = coverageOptions
            .find((option) => option.IsSelected);
          const extraLabel = selectedBundle?.TitleText
            || t('paymentBreakdown.notIncluded');

          powerupBaseResult.value = extraLabel;
          powerupBaseResult.price = selectedBundle?.FeeAmount || 0;
          powerupBaseResult.selected = !!selectedBundle;

          return powerupBaseResult;
        }
        powerupBaseResult.value = powerUp.IsSelected
          ? t('paymentBreakdown.included')
          : t('paymentBreakdown.notIncluded');
        return powerupBaseResult;
      });

    return powerUpsFilter;
  }, [quoteRate, t, isProp103]);

  const { InsuranceProductFeeEB } = data.insuranceProductEB;

  const oneTimeFee = useMemo(() => {
    const { oneTime } = BILLING_CYCLE_TYPE;

    const fee = InsuranceProductFeeEB
      .find((feeEB) => feeEB.InsuranceFeeEB.BillingCycleType === oneTime);

    return fee;
  }, [InsuranceProductFeeEB]);

  const installmentFee = useMemo(() => {
    const { annualy, monthly } = BILLING_CYCLE_TYPE;
    const billingCycle = isMonthlyPlan ? monthly : annualy;

    const fee = InsuranceProductFeeEB
      .find((feeEB) => feeEB.InsuranceFeeEB.BillingCycleType === billingCycle
        && feeEB.InsuranceFeeEB.PaymentType === paymentMethodType?.value);

    return fee;
  }, [InsuranceProductFeeEB, isMonthlyPlan, paymentMethodType]);

  const taxes = useMemo(() => {
    const quoteSummary = rateSummary.PetQuotes
      .find((item) => item.PetQuoteId === quoteRate.petQuoteId) || {};
    return isMonthlyPlan
      ? quoteSummary.MonthlyTaxes
      : quoteSummary.AnnualTaxes;
  }, [isMonthlyPlan, quoteRate, rateSummary]);

  const taxesItems = useMemo(() => {
    if (!taxes) {
      return null;
    }

    return (
      taxes.map((item) => (
        <BreakdownItem
          key={String(`${item.Description}${item.Amount}`)}
          bold
          label={item.Description}
          value={`$${currencyFormat({ value: item.Amount })}`}
        />
      ))
    );
  }, [taxes]);

  const taxesReduced = useMemo(() => {
    if (!taxes) {
      return 0;
    }

    return taxes.reduce((prev, next) => (prev + next.Amount), 0);
  }, [taxes]);

  const total = useMemo(() => {
    const { annualAmountPlan, monthlyAmountPlan } = quote;
    const taxesTotal = taxesReduced;
    let oneTimeAmount = 0;
    let installmentAmount = 0;

    if (oneTimeFee) {
      oneTimeAmount = oneTimeFee.InsuranceFeeEB.Amount;
    }

    if (installmentFee) {
      installmentAmount = installmentFee.InsuranceFeeEB.Amount;
    }

    const baseTotal = isMonthlyPlan ? monthlyAmountPlan : annualAmountPlan;

    return baseTotal + installmentAmount + oneTimeAmount + taxesTotal;
  }, [installmentFee, isMonthlyPlan, oneTimeFee, quote, taxesReduced]);

  const productPremiumPrice = useMemo(() => {
    const { monthlyAmountPlan, annualAmountPlan } = quote;
    if (isProp103) {
      const sumModifierPrices = modifiersMapped
        .reduce((prev, next) => (prev + next.price), 0);

      return isMonthlyPlan
        ? monthlyAmountPlan - sumModifierPrices
        : annualAmountPlan - (sumModifierPrices * 12);
    }
    return isMonthlyPlan ? monthlyAmountPlan : annualAmountPlan;
  }, [isMonthlyPlan, isProp103, quote, modifiersMapped]);

  const petContainerId = `petbreakdown-${quoteRate.petQuoteId}`;

  const hasYearAge = quoteRate.petAgeName?.toLowerCase().includes('year');
  const petAgeCount = Number(getAgeInYears(quoteRate.petAgeName));

  return (
    <div
      key={String(quoteRate.petQuoteId)}
      className="Column Payment-breakdown-pet-row-container"
    >
      <button
        aria-controls={petContainerId}
        aria-expanded={isExpanded}
        className="Row Full-width-mobile Payment-breakdown-toggle"
        onClick={handleExpanded}
        type="button"
      >
        <span className="Payment-breakdown-pet-tag-container">
          <TagPetCover
            className="Payment-breakdown-pet-tag"
            petType={petTag.petType}
          />
        </span>

        <span className="Payment-breakdown-pet-info">
          <span className="Payment-breakdown-pet-info-left">
            <span
              className="Payment-breakdown-pet-name"
            >
              {petTag.petName}
            </span>

            <span className="Payment-breakdown-breed">
              {quoteRate.breedName}
              {' | '}
              <span className="Payment-breakdown-age-full">
                {hasYearAge
                  ? t('paymentBreakdown.petAge', { count: petAgeCount })
                  : quoteRate.petAgeName}
              </span>

              <span className="Payment-breakdown-age-abbr">
                {hasYearAge
                  ? t('paymentBreakdown.petAgeAbbr', { count: petAgeCount })
                  : quoteRate.petAgeName}
              </span>
              &nbsp;| {quoteRate.genderName}
            </span>
          </span>

          <img
            alt=""
            aria-hidden
            className="Payment-breakdown-action"
            src={isExpanded ? collapseIcon : expandIcon}
          />
        </span>
      </button>

      <div
        className={'Payment-breakdown-expandable-container'
          + ` ${isExpanded ? '' : 'Payment-breakdown-hidden'}`}
        id={petContainerId}
      >
        <div className="Payment-breakdown-expandable">
          <div className="Payment-breakdown-column">
            <h2
              className="Payment-breakdown-list-title"
              id={`${petContainerId}-summary`}
            >
              {t('paymentBreakdown.planSummary')}
            </h2>

            <ul
              aria-labelledby={`${petContainerId}-summary`}
              className="Payment-breakdown-list"
            >
              <BreakdownItem
                label={t('paymentBreakdown.annualLimit')}
                value={quote.selectedPlanAmount.toLowerCase()}
              />

              <BreakdownItem
                label={t('paymentBreakdown.annualDeductible')}
                value={quote.deductibleQuantity}
              />

              <BreakdownItem
                label={t('paymentBreakdown.reimbursement')}
                value={quote.reimbursementPercentage}
              />

              {modifiersMapped.map((modifier) => (
                <BreakdownItem
                  label={modifier.label}
                  value={modifier.value}
                />
              ))}
            </ul>
          </div>

          <div className="Payment-breakdown-column">
            <h2
              className="Payment-breakdown-list-title"
              id={`${petContainerId}-cost`}
            >
              {isMonthlyPlan
                ? t('paymentBreakdown.monthlyCostBreakdown')
                : t('paymentBreakdown.annualCostBreakdown')}
            </h2>

            <ul
              aria-labelledby={`${petContainerId}-cost`}
              className="Payment-breakdown-list"
            >
              <BreakdownItem
                label={t(`paymentBreakdown.${isMonthlyPlan
                  ? 'monthlyPremium' : 'annualPremium'}`)}
                value={`$${currencyFormat({ value: productPremiumPrice })}`}
              />

              {isProp103 && modifiersMapped
                .filter((modifier) => modifier.selected)
                .map((modifier) => (
                  <BreakdownItem
                    label={`${modifier.value} ${modifier.label}`}
                    value={`$${currencyFormat({
                      value: isMonthlyPlan
                        ? modifier.price
                        : modifier.price * 12,
                    })}`}
                  />
                ))}

              {installmentFee && (
                <BreakdownItem
                  label={installmentFee.InsuranceFeeEB.DisplayName}
                  value={`$${currencyFormat({
                    value: installmentFee.InsuranceFeeEB.Amount,
                  })}`}
                />
              )}

              {oneTimeFee && (
                <BreakdownItem
                  label={oneTimeFee.InsuranceFeeEB.DisplayName}
                  value={`$${currencyFormat({
                    value: oneTimeFee.InsuranceFeeEB.Amount,
                  })}`}
                />
              )}

              {taxesItems}

              <BreakdownItem
                bold
                label={`${petTag.petName}’s Total`}
                noCapitalize
                toBottom
                value={`$${currencyFormat({ value: total })}`}
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentBreakdown = () => {
  const { t } = useTranslations('quoting');
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const {
    data,
    isMonthlyPlan,
  } = store;

  return (
    <div
      className="Payment-breakdown-container"
      id="payment-breakdown"
    >
      <h1 className="Payment-breakdown-title">{t('petPolicies')}</h1>

      {data.ebPetQuoteResponseList.map((quoteRate) => (
        <PetBreakdown
          key={String(quoteRate.petQuoteId)}
          isMonthlyPlan={isMonthlyPlan}
          quoteRate={quoteRate}
        />
      ))}
    </div>
  );
};

export default PaymentBreakdown;
